import BaseModel from './BaseModel';
import { v4 } from 'uuid';
import FormLayoutField from './FormLayoutField';
import FormLayoutFieldGroup from './FormLayoutFieldGroup';
import FormLayoutStep from './FormLayoutStep';
import ImageField from './FormLayoutFields/ImageField';
import {
    TextField, LongTextField,
    RichTextField, NumberField,
    BooleanField, DatetimeField,
    FileField,
    ListField,
    PasswordField, PhoneNumberField,
    SectorsSelectorField, ActivitiesSelectorField,
    AvailabilitiesSelectorField,
    StaticTextField,
    StaticImageField,
    HiddenField,
    LanguageField,
    CountriesFormField,
    TickettypePricingField,
} from './FormLayoutFields';
import * as _ from 'es-toolkit';
import Utils from '../services/Utils';
import { getFieldsOrderedForKoreApi } from '../services/formLayoutGroupUtils';

/** Class representing a form layout. */
class FormLayout extends BaseModel {
    /* Semantic form types (Kore `forms.type`) */
    static TYPE_APPLICATION = 'application';
    static TYPE_ACCREDITATION = 'accreditation';
    static TYPE_PURCHASE = 'purchase';

    /* Types */
    static FIELD    = 'field';

    /* Fields types */
    static TEXT         = 'text';
    static LONGTEXT     = 'longtext';
    static RICHTEXT     = 'richtext';
    static PHONE_NUMBER = 'phone';
    static PASSWORD     = 'password';
    static NUMBER       = 'number';
    static DATETIME     = 'datetime';
    static BOOLEAN      = 'boolean';
    static LIST         = 'list';
    static FILE         = 'file';
    static IMAGE        = 'image';
    static SECTORS      = 'sectors';
    static ACTIVITIES   = 'activities';
    static AVAILABILITIES = 'availabilities';
    static STATIC_TEXT  = 'static_text';
    static STATIC_IMAGE = 'static_image';
    static HIDDEN       = 'hidden';
    static LANGUAGE     = 'language';
    static COUNTRIES    = 'countries';
    static TICKETTYPE_PRICING = 'tickettype_pricing';

    /* Options */
    static OPTION_NAME       = 'name';
    static OPTION_LABEL      = 'label';
    static OPTION_DESCRIPTION = 'description';
    static OPTION_WIDTH      = 'width';
    static OPTION_REQUIRED   = 'required';
    static OPTION_ROWS       = 'rows';
    static OPTION_MIN        = 'min';
    static OPTION_MAX        = 'max';
    static OPTION_STEP       = 'step';
    static OPTION_WITH_TIME  = 'with_time';
    static OPTION_DATE_CONSTRAINT = 'date_constraint';
    static OPTION_DATE_MIN = 'date_min';
    static OPTION_DATE_MAX = 'date_max';
    static OPTION_OPEN_AT_YEARS_AGO = 'open_at_years_ago';
    static OPTION_CHOICES    = 'choices';
    static OPTION_MULTIPLE   = 'multiple';
    /** Richtext: store/edit one value per instance language (map) vs form locale scalar. */
    static OPTION_MULTILINGUAL = 'multilingual';
    static OPTION_EXPANDED   = 'expanded';
    static OPTION_EXTENSIONS = 'extensions';
    static OPTION_CONTENT    = 'content';
    /** Static image: upload data URL and/or settings library type. */
    static OPTION_STATIC_IMAGE = 'static_image';
    /** Default values (ISO codes) for some field types, stored in `field.content.defaults`. */
    static OPTION_DEFAULTS   = 'defaults';
    static OPTION_PLACEHOLDER = 'placeholder';
    static OPTION_NO_DIALOG  = 'showNoDialog';
    /** Default value for hidden fields, stored in `field.content.default`. */
    static OPTION_DEFAULT_VALUE = 'default_value';
    /** Availabilities field: manual calendar vs period slots. */
    static OPTION_AVAILABILITY_MODE = 'availability_mode';
    static OPTION_AVAILABILITY_PHASES = 'availability_phases';
    static OPTION_AVAILABILITY_PERIODS = 'availability_periods';
    /** Tickettype+pricing field: also pick a pricing. */
    static OPTION_WITH_PRICING = 'with_pricing';
    /** Semantic submit name for the tickettype id (default tickettype_id). */
    static OPTION_TICKETTYPE_NAME = 'tickettype_name';
    /** Semantic submit name for the pricing id (default pricing_id). */
    static OPTION_PRICING_NAME = 'pricing_name';
    /** Allowlist of pricing keys offered when with_pricing is on. */
    static OPTION_PRICING_CHOICES = 'pricing_choices';

    /** Display-only widgets (no respondent answer). */
    static isDisplayOnlyField(type) {
        return type === FormLayout.STATIC_TEXT || type === FormLayout.STATIC_IMAGE;
    }

    static createField(properties) {
        switch (properties.type) {
            case FormLayout.TEXT:
                return new TextField(properties);
            case FormLayout.LONGTEXT:
                return new LongTextField(properties);
            case FormLayout.RICHTEXT:
                return new RichTextField(properties);
            case FormLayout.PHONE_NUMBER:
                return new PhoneNumberField(properties);
            case FormLayout.PASSWORD:
                return new PasswordField(properties);
            case FormLayout.NUMBER:
                return new NumberField(properties);
            case FormLayout.DATETIME:
                return new DatetimeField(properties);
            case FormLayout.BOOLEAN:
                return new BooleanField(properties);
            case FormLayout.FILE:
                return new FileField(properties);
            case FormLayout.IMAGE:
                return new ImageField(properties);
            case FormLayout.LIST:
                return new ListField(properties);
            case FormLayout.SECTORS:
                return new SectorsSelectorField(properties);
            case FormLayout.ACTIVITIES:
                return new ActivitiesSelectorField(properties);
            case FormLayout.AVAILABILITIES:
                return new AvailabilitiesSelectorField(properties);
            case FormLayout.STATIC_TEXT:
                return new StaticTextField(properties);
            case FormLayout.STATIC_IMAGE:
                return new StaticImageField(properties);
            case FormLayout.HIDDEN:
                return new HiddenField(properties);
            case FormLayout.LANGUAGE:
                return new LanguageField(properties);
            case FormLayout.COUNTRIES:
                return new CountriesFormField(properties);
            case FormLayout.TICKETTYPE_PRICING:
                return new TickettypePricingField(properties);
            default:
                return null;
        }
    }

    static getInputFields() {
        return [
            new TextField(),
            new LongTextField(),
            new RichTextField(),
            new PhoneNumberField(),
            new PasswordField(),
            new NumberField(),
            new DatetimeField(),
            new BooleanField(),
            new FileField(),
            new ImageField(),
            new ListField(),
            new SectorsSelectorField(),
            new ActivitiesSelectorField(),
            new AvailabilitiesSelectorField(),
            new LanguageField(),
            new CountriesFormField(),
            new TickettypePricingField(),
        ];
    }

    static getPresentationFields() {
        return [
            new StaticTextField(),
            new StaticImageField(),
            new HiddenField(),
        ];
    }

    static getAvailableFields(_type) {
        return [
            ...FormLayout.getPresentationFields(),
            ...FormLayout.getInputFields(),
        ];
    }

    static defaultLayout() {
        const labels = { fr: 'Formulaire', en: 'Form', de: 'Form' };
        const lang = Utils.default_lang();
        return {
            id: v4(),
            name: Utils.new_pricing_names(labels[lang] || labels.en || 'Form', lang),
            type: null,
            fields: [],
            groups: [],
            steps: [],
            stepTransition: 'fade',
            breadcrumbStyle: 'dots',
            showStepTitle: true,
            ui_options: {},
        };
    }

    static types = () => [
        FormLayout.TYPE_APPLICATION,
        FormLayout.TYPE_ACCREDITATION,
        FormLayout.TYPE_PURCHASE,
    ];

    /**
     * @param {object[]} forms
     * @param {string[]} types
     * @returns {object[]}
     */
    static filter_by_types(forms, types) {
        if (!Array.isArray(forms))
            return [];
        if (!Array.isArray(types) || types.length === 0)
            return forms;
        return forms.filter((form) => types.includes(form?.type));
    }

    constructor(properties) {
        super(properties);

        const ui = this.ui_options && typeof this.ui_options === 'object' ? this.ui_options : {};
        if (this.stepTransition === undefined && ui.step_transition != null) {
            this.stepTransition = ui.step_transition;
        }
        if (this.step_transition != null && this.stepTransition === undefined) {
            this.stepTransition = this.step_transition;
        }
        delete this.step_transition;
        if (this.stepTransition === undefined || this.stepTransition === null) {
            this.stepTransition = 'fade';
        }

        if (this.breadcrumbStyle === undefined && ui.breadcrumb_style != null) {
            this.breadcrumbStyle = ui.breadcrumb_style;
        }
        if (this.breadcrumb_style != null && this.breadcrumbStyle === undefined) {
            this.breadcrumbStyle = this.breadcrumb_style;
        }
        delete this.breadcrumb_style;
        if (this.breadcrumbStyle === undefined || this.breadcrumbStyle === null) {
            this.breadcrumbStyle = 'dots';
        }

        if (this.showStepTitle === undefined && ui.show_step_title != null) {
            this.showStepTitle = !!ui.show_step_title;
        }
        if (this.show_step_title != null && this.showStepTitle === undefined) {
            this.showStepTitle = !!this.show_step_title;
        }
        delete this.show_step_title;
        if (this.showStepTitle === undefined || this.showStepTitle === null) {
            this.showStepTitle = true;
        }

        this.steps = (this.steps || []).map((s) => new FormLayoutStep(s));
        this.groups = (this.groups || []).map((g) => new FormLayoutFieldGroup(g));

        this.fields = (this.fields || []).map(f => {
            switch (f.type) {
                case FormLayout.TEXT:
                    return new TextField(f);
                case FormLayout.LONGTEXT:
                    return new LongTextField(f);
                case FormLayout.RICHTEXT:
                    return new RichTextField(f);
                case FormLayout.PHONE_NUMBER:
                    return new PhoneNumberField(f);
                case FormLayout.PASSWORD:
                    return new PasswordField(f);
                case FormLayout.NUMBER:
                    return new NumberField(f);
                case FormLayout.DATETIME:
                    return new DatetimeField(f);
                case FormLayout.BOOLEAN:
                    return new BooleanField(f);
                case FormLayout.FILE:
                    return new FileField(f);
                case FormLayout.IMAGE:
                    return new ImageField(f);
                case FormLayout.LIST:
                    return new ListField(f);
                case FormLayout.SECTORS:
                    return new SectorsSelectorField(f);
                case FormLayout.ACTIVITIES:
                    return new ActivitiesSelectorField(f);
                case FormLayout.AVAILABILITIES:
                    return new AvailabilitiesSelectorField(f);
                case FormLayout.STATIC_TEXT:
                    return new StaticTextField(f);
                case FormLayout.STATIC_IMAGE:
                    return new StaticImageField(f);
                case FormLayout.HIDDEN:
                    return new HiddenField(f);
                case FormLayout.LANGUAGE:
                    return new LanguageField(f);
                case FormLayout.COUNTRIES:
                    return new CountriesFormField(f);
                case FormLayout.TICKETTYPE_PRICING:
                    return new TickettypePricingField(f);
                default:
                    return new FormLayoutField(f);
            }
        });
    }

    /**
     * Prepare this object for duplicate.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForDuplicate() {
        const prepared = _.cloneDeep(this);

        prepared.id = v4();

        const groupIdMap = {};
        const stepIdMap = {};
        prepared.steps = (prepared.steps || []).map((step_raw) => {
            const base = _.cloneDeep(step_raw);
            const step = { ...base };
            const oldSid = step.id;
            step.id = v4();
            stepIdMap[oldSid] = step.id;
            return step;
        });

        prepared.groups = (prepared.groups || []).map((group_raw) => {
            const group = { ...group_raw };
            const oldId = group.id;
            group.id = v4();
            groupIdMap[oldId] = group.id;
            const sid = group.stepId ?? group.step_id;
            if (sid && stepIdMap[sid]) {
                group.stepId = stepIdMap[sid];
            }
            delete group.step_id;
            return group;
        });

        prepared.fields = prepared.fields.map(field => {
            field.id      = v4();
            field.form_id = prepared.id;

            const gid = field.groupId ?? field.group_id;
            if (gid && groupIdMap[gid]) {
                field.groupId = groupIdMap[gid];
            }
            delete field.group_id;

            const fsid = field.stepId ?? field.step_id;
            if (fsid && stepIdMap[fsid]) {
                field.stepId = stepIdMap[fsid];
            }
            delete field.step_id;

            field.choices = (field.choices || []).map(choice => {
                choice.id       = v4();
                choice.field_id = field.id;

                return choice;
            });

            return field;
        });

        return prepared;
    }

    /**
     * Kore persists field order from the **JSON array order** of `fields`, not from each
     * field's `position`. Reorder so drag-and-drop / layout matches after save.
     */
    prepareForUpdate() {
        const prepared = super.prepareForUpdate();
        const groups = prepared.groups || [];
        prepared.groups = [...groups].sort((a, b) => (a.position || 0) - (b.position || 0));
        const stepsPlain = (prepared.steps || []).map((s) => (
            s instanceof FormLayoutStep ? s.toPlainObject() : _.cloneDeep(s)
        ));
        prepared.steps = [...stepsPlain].sort((a, b) => (a.position || 0) - (b.position || 0));
        prepared.fields = getFieldsOrderedForKoreApi(prepared.fields, prepared.groups, prepared.steps);

        (prepared.fields || []).forEach((field) => {
            Utils.cleanTranslatableProperty(field, 'label');
            Utils.cleanTranslatableProperty(field, 'description');
            Utils.cleanTranslatableProperty(field, 'placeholder');
            // `content` is a translations map only for static texts. Other types store
            // plain settings in it (image: max_width/max_height, hidden: default, ...)
            // which the translations cleanup would drop.
            if (field.type === FormLayout.STATIC_TEXT)
                Utils.cleanTranslatableProperty(field, 'content');
            // Language UI flag is not a Kore column — fold into content before PUT.
            if (field.type === FormLayout.LANGUAGE) {
                const content = field.content && typeof field.content === 'object' && !Array.isArray(field.content)
                    ? { ...field.content }
                    : {};
                if (Object.prototype.hasOwnProperty.call(field, 'showNoDialog')) {
                    content.show_no_dialog = !!field.showNoDialog;
                }
                field.content = content;
                delete field.showNoDialog;
            }
            (field.choices || []).forEach((choice) => {
                Utils.cleanTranslatableProperty(choice, 'label');
            });
        });
        (prepared.steps || []).forEach((step) => {
            Utils.cleanTranslatableProperty(step, 'label');
            Utils.cleanTranslatableProperty(step, 'breadcrumbLabel');
            Utils.cleanTranslatableProperty(step, 'nextLabel');
        });
        (prepared.groups || []).forEach((group) => {
            Utils.cleanTranslatableProperty(group, 'label');
            Utils.cleanTranslatableProperty(group, 'addRowLabel');
            Utils.cleanTranslatableProperty(group, 'removeRowLabel');
        });

        prepared.ui_options = {
            step_transition: prepared.stepTransition ?? 'fade',
            breadcrumb_style: prepared.breadcrumbStyle ?? 'dots',
            show_step_title: prepared.showStepTitle !== false,
        };
        delete prepared.stepTransition;
        delete prepared.breadcrumbStyle;
        delete prepared.showStepTitle;
        return prepared;
    }
}

/**
 * Get the fields to get server side when we
 * ask for a listing
 */
FormLayout.getListingFields = () => ['id', 'name', 'type'];

export default FormLayout;
